import { useMutation } from '@apollo/client'
import { Alert, ButtonLink } from '@designsforhealth/dfh-react-components'
import { CircularProgress } from '@material-ui/core'
import React, { useContext, useEffect, useState } from 'react'
import styled from 'styled-components'

import {
  CartFragment,
  RemoveCouponFromCartDocument,
  SetDispensaryPromoCodeOnCartDocument,
} from '../../graphql/magento'
import useIsMounted from '../../hooks/useIsMounted'
import useLogAndCaptureError from '../../hooks/useLogAndCaptureError'
import { TokenContext } from '../../layouts/context'
import { CartDispensaryPromoCode } from '../../lib/cart/dispensary-promo'
import { usePromoCodeUtilities } from '../../lib/cart/hooks/usePromoCodeUtilities'
import { CartState, ToggleCodesAction } from '../../lib/cart/reducers/cart-state'
import { logAndCaptureException } from '../../utils/errorTools'
import { checkAndDiscardQueryResponse } from '../../utils/graphqlTools'
import ErrorUpdatingCartMessage from '../ErrorUpdatingCartMessage'
import { PlusMinusIcon } from '../PlusMinusIcon'

const StyledCouponCodeList = styled.ul`
  list-style: none;
  overflow: hidden;
  width: 100%;
  // mirror styling from summary section
  margin: 7px 0 0;
  padding: 0;
`

const StyledCouponCode = styled.li`
  margin-bottom: 10px;
  width: 100%;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`

const StyledCollapsedCouponCodes = styled.div`
  flex: 1;
  text-align: right;
  width: 100%;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`

const StyledPlusMinusIcon = styled(PlusMinusIcon)`
  margin-left: 8px;
`

const StyledCollapseButton = styled(ButtonLink)`
  // increase touch target size
  padding: 10px 0;
  font-weight: 500;
  margin-right: 16px;
  white-space: nowrap;
`

export interface AppliedPromoCodeProps extends Pick<CartState, 'codesCollapsed'> {
  appliedCoupons: NonNullable<CartFragment['applied_coupons']>
  cartDispensaryPromoCodes: CartDispensaryPromoCode[] | undefined
  cartId: string
  className?: string
  dispatch: React.Dispatch<ToggleCodesAction>
}

const AppliedPromoCode: React.FC<AppliedPromoCodeProps> = ({
  appliedCoupons,
  cartId,
  className,
  cartDispensaryPromoCodes,
  codesCollapsed,
  dispatch,
}) => {
  const token = useContext(TokenContext)
  const [removeCoupon, { loading: removeCouponLoading, error: removeCouponError }] = useMutation(
    RemoveCouponFromCartDocument,
    {
      context: { token },
    },
  )
  useLogAndCaptureError(removeCouponError)

  const [
    setDispensaryPromoCode,
    { loading: setDispensaryPromoCodeLoading, error: setDispensaryPromoCodeError },
  ] = useMutation(SetDispensaryPromoCodeOnCartDocument, {
    context: { token },
  })
  useLogAndCaptureError(setDispensaryPromoCodeError)

  const { concealCode, loading: utilitiesLoading } = usePromoCodeUtilities({
    cartDispensaryPromoCodes,
  })
  const [concealedCodes, setConcealedCodes] = useState<string[]>([])
  const isMounted = useIsMounted()

  useEffect(() => {
    if (!appliedCoupons || utilitiesLoading) {
      setConcealedCodes([])
      return
    }
    appliedCoupons
      .reduce(async (acc: Promise<string[]>, coupon) => {
        if (!coupon) return acc
        const awaitedAcc = await acc
        awaitedAcc.push(await concealCode(coupon.code))
        return awaitedAcc
      }, Promise.resolve([]))
      .then((value) => {
        if (isMounted.current) {
          setConcealedCodes(value)
        }
      })
      .catch(logAndCaptureException)
  }, [appliedCoupons, concealCode, isMounted, utilitiesLoading])

  // TODO: For removal of individual coupons, pass 'couponCode' parameter to function and variable.
  //  The GraphQL schema and resolver have already been updated to support this functionality
  //  Pass the couponCode in the onClick property of the button below: onClick={() => handleRemoveCoupon(coupon.code)}
  //  Move the button component inside the map of appliedCoupons below
  const handleRemoveAllCoupons = () => {
    removeCoupon({
      variables: { cartId },
    })
      .then(checkAndDiscardQueryResponse)
      .then(() =>
        setDispensaryPromoCode({
          variables: { cartId, dispensaryPromoCode: '' },
        }),
      )
      .catch(() => {
        // no-op
      })
  }

  return (
    <div className={className}>
      <StyledCollapseButton onClick={() => dispatch({ type: 'toggleCodes' })}>
        Applied promo
        <StyledPlusMinusIcon minus={!codesCollapsed} />
      </StyledCollapseButton>
      {codesCollapsed ? (
        <StyledCollapsedCouponCodes>{concealedCodes.join(', ')}</StyledCollapsedCouponCodes>
      ) : (
        <>
          <ButtonLink
            disabled={removeCouponLoading || setDispensaryPromoCodeLoading}
            underline
            onClick={handleRemoveAllCoupons}
          >
            {removeCouponLoading || setDispensaryPromoCodeLoading ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              'Remove All'
            )}
          </ButtonLink>
          <StyledCouponCodeList>
            {concealedCodes.map((text) => (
              <StyledCouponCode key={text}>{text}</StyledCouponCode>
            ))}
          </StyledCouponCodeList>
        </>
      )}
      {removeCouponError && (
        <Alert type="error">
          <ErrorUpdatingCartMessage />
        </Alert>
      )}
    </div>
  )
}

const StyledAppliedPromoCode = styled(AppliedPromoCode)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  width: 100%;

  ${Alert} {
    margin-top: 15px;
  }
`

export default StyledAppliedPromoCode
